// Port of TrailData/TrailDataError.swift: errors thrown while loading bundled data or importing geodata files.
// Associated strings are diagnostic (file names, format names, parser messages), not user-facing sentences; the UI
// maps each `reason.kind` to localized text.

export type TrailDataErrorReason =
  /** A required data file is missing (the file name, e.g. `"pois.json"` or `"trails/tmb.json"`). */
  | { kind: 'fixtureMissing'; file: string }
  /** A file exists but could not be read or decoded. */
  | { kind: 'decoding'; file: string; message: string }
  /** The file type, document type or coordinate reference system is not supported. */
  | { kind: 'unsupportedFormat'; format: string }
  /** The file parsed correctly but contains no usable geometry. */
  | { kind: 'noGeometry' };

function describe(reason: TrailDataErrorReason): string {
  switch (reason.kind) {
    case 'fixtureMissing':
      return `missing data file ${reason.file}`;
    case 'decoding':
      return `cannot decode ${reason.file}: ${reason.message}`;
    case 'unsupportedFormat':
      return `unsupported format: ${reason.format}`;
    case 'noGeometry':
      return 'no usable geometry';
  }
}

export class TrailDataError extends Error {
  constructor(readonly reason: TrailDataErrorReason) {
    super(describe(reason));
    this.name = 'TrailDataError';
  }

  /** Developer-facing description (logs), as Swift `description`. */
  get description(): string {
    return describe(this.reason);
  }

  static fixtureMissing(file: string): TrailDataError {
    return new TrailDataError({ kind: 'fixtureMissing', file });
  }

  static decoding(file: string, message: string): TrailDataError {
    return new TrailDataError({ kind: 'decoding', file, message });
  }

  static unsupportedFormat(format: string): TrailDataError {
    return new TrailDataError({ kind: 'unsupportedFormat', format });
  }

  static noGeometry(): TrailDataError {
    return new TrailDataError({ kind: 'noGeometry' });
  }
}

/** Message of any thrown value (the `compactDescription` of a `DecodingError`). */
export function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
